(function giftScriptFactory() {
  const GIFT_VARIANT_ID = 53707094720788;
  const GIFT_PRODUCT_ID = 10351515861268;
  const EXCLUDED_PRODUCT_ID = 10272055427348;
  const GIFT_PROPERTY = '_CASEKOO Sticker Gift';
  const GIFT_PROPERTY_VALUE = 'Yes';
  // Retired campaign products must never survive in an older customer cart.
  const RETIRED_GIFT_PRODUCT_IDS = new Set([
    10315847500052, 10315847532820, 10315847598356,
  ]);
  const isRetiredGift = (item) =>
    RETIRED_GIFT_PRODUCT_IDS.has(Number(item.product_id)) ||
    item.properties?.['_CASEKOO Gift'] === 'anniversary-2026';
  const SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';

  let syncInProgress = false;
  let syncAgain = false;
  let syncTimer;

  const delay = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const normalizeRoot = (root) => {
    if (!root) return null;

    const normalized = root.startsWith('/') ? root : `/${root}`;
    return normalized.endsWith('/') ? normalized : `${normalized}/`;
  };

  const localizedRoot = () => {
    const themeCartUrl = window.theme?.routes?.cart_url;
    if (themeCartUrl) {
      const rootFromCart = themeCartUrl.replace(/cart(?:\.js)?\/?$/, '');
      const normalizedThemeRoot = normalizeRoot(rootFromCart);
      if (normalizedThemeRoot) return normalizedThemeRoot;
    }

    const shopifyRoot = normalizeRoot(window.Shopify?.routes?.root);
    if (shopifyRoot) return shopifyRoot;

    const localePathMatch = window.location.pathname.match(
      /^\/([a-z]{2}(?:-[a-z]{2})?)\//i
    );

    return localePathMatch ? `/${localePathMatch[1]}/` : '/';
  };

  const jsonRoute = (url) => (url.endsWith('.js') ? url : `${url}.js`);

  const routes = () => {
    const root = localizedRoot();
    const themeRoutes = window.theme?.routes || {};

    return {
      cart: jsonRoute(themeRoutes.cart_url || `${root}cart`),
      add: jsonRoute(themeRoutes.cart_add_url || `${root}cart/add`),
      update: jsonRoute(themeRoutes.cart_update_url || `${root}cart/update`),
      checkout: themeRoutes.checkout_url || `${root}checkout`,
    };
  };

  const requestOptions = (body) => ({
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });

  const sectionRequest = (body = {}) => ({
    ...body,
    sections: SECTIONS,
    sections_url: window.location.pathname,
  });

  const parseResponse = async (response) => {
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(
        data.description || data.message || 'Unable to update the cart.'
      );
    }

    return data;
  };

  const fetchCart = async () => {
    const response = await fetch(routes().cart, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin',
    });

    return parseResponse(response);
  };

  const updateCart = async (updates) => {
    const response = await fetch(
      routes().update,
      requestOptions(sectionRequest({ updates }))
    );

    return parseResponse(response);
  };

  const addGifts = async (parentItems) => {
    if (!parentItems.length) return;

    const response = await fetch(
      routes().add,
      requestOptions(
        sectionRequest({
          items: parentItems.map((parentItem) => ({
            id: GIFT_VARIANT_ID,
            quantity: parentItem.quantity,
            properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE },
            parent_line_key: parentItem.key,
          })),
        })
      )
    );

    return parseResponse(response);
  };

  const announceCartUpdate = (response) => {
    if (!response?.sections) return;

    window.dispatchEvent(
      new CustomEvent('shapes:cart:update', {
        detail: {
          response,
          casekooStickerGift: true,
        },
      })
    );
  };

  const isMarkedGift = (item) =>
    item.product_id === GIFT_PRODUCT_ID &&
    item.properties?.[GIFT_PROPERTY] === GIFT_PROPERTY_VALUE;

  const isEligiblePhoneCase = (item) => {
    const searchableText = [
      item.handle,
      item.product_title,
      item.title,
      item.url,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return (
      item.quantity > 0 &&
      !isRetiredGift(item) &&
      item.product_id !== GIFT_PRODUCT_ID &&
      item.product_id !== EXCLUDED_PRODUCT_ID &&
      /(^|[^a-z])(case|cases|cover|covers)([^a-z]|$)/.test(searchableText)
    );
  };

  const getEligibleProducts = (cart) => cart.items.filter(isEligiblePhoneCase);

  const synchronizeGift = async () => {
    if (syncInProgress) {
      syncAgain = true;
      const waitStartedAt = Date.now();

      while (syncInProgress && Date.now() - waitStartedAt < 5000) {
        await delay(50);
      }

      if (syncInProgress) {
        throw new Error('Gift synchronization timed out.');
      }

      return synchronizeGift();
    }

    syncInProgress = true;

    try {
      let cart = await fetchCart();
      const retiredItems = cart.items.filter(isRetiredGift);
      if (retiredItems.length) {
        const cleanedCart = await updateCart(
          Object.fromEntries(retiredItems.map((item) => [item.key, 0]))
        );
        announceCartUpdate(cleanedCart);
        cart = await fetchCart();
        if (cart.items.some(isRetiredGift)) {
          throw new Error('Retired promotional items could not be removed.');
        }
      }
      const eligibleItems = getEligibleProducts(cart);
      const eligibleItemsByKey = new Map(
        eligibleItems.map((item) => [item.key, item])
      );
      const giftItems = cart.items.filter(
        (item) => item.product_id === GIFT_PRODUCT_ID
      );
      const matchedParentKeys = new Set();
      const updates = {};
      let response;

      giftItems.forEach((giftItem) => {
        const parentKey = giftItem.parent_relationship?.parent_key;
        const parentItem = eligibleItemsByKey.get(parentKey);

        if (
          !isMarkedGift(giftItem) ||
          !parentItem ||
          matchedParentKeys.has(parentKey)
        ) {
          updates[giftItem.key] = 0;
          return;
        }

        matchedParentKeys.add(parentKey);

        if (giftItem.quantity !== parentItem.quantity) {
          updates[giftItem.key] = parentItem.quantity;
        }
      });

      if (Object.keys(updates).length) {
        response = await updateCart(updates);
      }

      const missingGiftParents = eligibleItems.filter(
        (item) => !matchedParentKeys.has(item.key)
      );

      if (missingGiftParents.length) {
        response = await addGifts(missingGiftParents);
      }

      announceCartUpdate(response);

      return response ? fetchCart() : cart;
    } catch (error) {
      console.error('[CASEKOO Sticker Gift]', error);
      throw error;
    } finally {
      syncInProgress = false;

      if (syncAgain) {
        syncAgain = false;
        scheduleSync(100);
      }
    }
  };

  const scheduleSync = (delayMilliseconds = 250) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      synchronizeGift().catch(() => {});
    }, delayMilliseconds);
  };

  const scheduleRetryBurst = () => {
    [150, 700, 1500].forEach((delayMilliseconds) => {
      window.setTimeout(() => {
        synchronizeGift().catch(() => {});
      }, delayMilliseconds);
    });
  };

  [
    'shapes:cart:afteradditem',
    'shapes:cart:cartqtychange',
    'shapes:cart:update',
  ].forEach((eventName) => {
    window.addEventListener(eventName, (event) => {
      if (event.detail?.casekooStickerGift) return;
      scheduleSync();
    });
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const addTrigger = target.closest(
        'form[action*="/cart/add"] button[type="submit"], [data-casekoo-case-only-action]'
      );

      if (addTrigger) scheduleRetryBurst();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (/\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(form.action)) {
        scheduleRetryBurst();
      }
    },
    true
  );

  const isCartPage = () =>
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/?$/i.test(
      window.location.pathname
    );

  const scheduleInitialSync = () => {
    scheduleSync(isCartPage() ? 100 : 500);
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleSync(100);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialSync, {
      once: true,
    });
  } else {
    scheduleInitialSync();
  }
})();
